import axios from "axios";
import { observable, action, runInAction, decorate } from "mobx";
import ZonesApi from "./api";
import ZonesApiStub from "./stub";
import { DEFAULT_ZONE_TYPE, ZONE_TYPE_WAVE, USE_STUB_API } from "../../constants";

class ZonesStore {
    rootStore;
    apiClient;
    regionsRequest = undefined;

    list = [];
    total = 0;
    typeId = DEFAULT_ZONE_TYPE;
    name = "";
    pageNumber = 1;
    pageSize = 50;

    selected = undefined;
    regions = [];
    isFetchingRegionsInProgress = false;

    searchResults = [];
    searchTotal = 0;
    searchName = "";
    searchTypeId = undefined;
    searchLat = undefined;
    searchLng = undefined;
    searchPageNumber = 1;
    searchPageSize = 25;
    isSearchInProgress = false;
    searchError = undefined;

    isImportInProgress = false;
    importError = undefined;

    isFetchingListInProgress = false;
    fetchingListError = undefined;

    isFetchingRecordInProgress = false;
    fetchingRecordError = undefined;

    isSavingInProgress = false;
    savingError = undefined;

    isDeletingInProgress = false;
    deletingError = undefined;

    constructor(rootStore) {
        this.rootStore = rootStore;
        this.apiClient = USE_STUB_API ? new ZonesApiStub() : new ZonesApi(rootStore.apiClient);
    }

    async fetchList({ typeId, name, pageNumber, pageSize } = {}) {
        runInAction(() => {
            if (typeId !== undefined) this.typeId = typeId;
            if (name !== undefined) this.name = name;
            if (pageNumber !== undefined) this.pageNumber = pageNumber;
            if (pageSize !== undefined) this.pageSize = pageSize;
            this.isFetchingListInProgress = true;
            this.fetchingListError = undefined;
        });
        try {
            const data = await this.apiClient.list({
                typeId: this.typeId,
                name: this.name,
                pageNumber: this.pageNumber,
                pageSize: this.pageSize,
            });
            runInAction(() => {
                this.list = data?.items || data?.data || [];
                this.total = data?.total_count ?? data?.total ?? 0;
                this.isFetchingListInProgress = false;
            });
        } catch (err) {
            runInAction(() => {
                this.list = [];
                this.total = 0;
                this.fetchingListError = err;
                this.isFetchingListInProgress = false;
            });
        }
    }

    async searchZones({ name, typeId, lat, lng, pageNumber, pageSize } = {}) {
        runInAction(() => {
            if (name !== undefined) this.searchName = name;
            if (typeId !== undefined) this.searchTypeId = typeId;
            if (lat !== undefined) this.searchLat = lat;
            if (lng !== undefined) this.searchLng = lng;
            if (pageNumber !== undefined) this.searchPageNumber = pageNumber;
            if (pageSize !== undefined) this.searchPageSize = pageSize;
            this.isSearchInProgress = true;
            this.searchError = undefined;
        });
        try {
            const data = await this.apiClient.search({
                name: this.searchName,
                typeId: this.searchTypeId,
                lat: this.searchLat,
                lng: this.searchLng,
                pageNumber: this.searchPageNumber,
                pageSize: this.searchPageSize,
            });
            runInAction(() => {
                this.searchResults = data?.items || data?.data || [];
                this.searchTotal = data?.total_count ?? data?.total ?? 0;
                this.isSearchInProgress = false;
            });
        } catch (err) {
            runInAction(() => {
                this.searchResults = [];
                this.searchTotal = 0;
                this.searchError = err;
                this.isSearchInProgress = false;
            });
        }
    }

    async fetchRegions({ name } = {}) {
        if (this.regionsRequest) this.regionsRequest.cancel();
        const request = axios.CancelToken.source();
        this.regionsRequest = request;
        runInAction(() => {
            this.isFetchingRegionsInProgress = true;
        });
        try {
            const data = await this.apiClient.regions({ name, cancelToken: request.token });
            runInAction(() => {
                this.regions = data?.items || [];
                this.isFetchingRegionsInProgress = false;
            });
        } catch (err) {
            if (this.regionsRequest !== request) return;
            runInAction(() => {
                this.regions = [];
                this.isFetchingRegionsInProgress = false;
            });
        }
    }

    async importZone({ name, geometry, typeId = ZONE_TYPE_WAVE, timezone, oldRegionId }) {
        runInAction(() => {
            this.isImportInProgress = true;
            this.importError = undefined;
        });
        try {
            const data = await this.apiClient.create({ name, typeId, geometry, timezone, oldRegionId });
            runInAction(() => {
                this.isImportInProgress = false;
            });
            return data;
        } catch (err) {
            runInAction(() => {
                this.importError = err;
                this.isImportInProgress = false;
            });
            throw err;
        }
    }

    async fetchRecord(id) {
        runInAction(() => {
            this.isFetchingRecordInProgress = true;
            this.fetchingRecordError = undefined;
        });
        try {
            const data = await this.apiClient.getById(id, { withGeometry: true });
            runInAction(() => {
                this.selected = data;
                this.isFetchingRecordInProgress = false;
            });
            return data;
        } catch (err) {
            runInAction(() => {
                this.fetchingRecordError = err;
                this.isFetchingRecordInProgress = false;
            });
        }
    }

    async saveRecord(id, record) {
        runInAction(() => {
            this.isSavingInProgress = true;
            this.savingError = undefined;
        });
        try {
            const data = id
                ? await this.apiClient.update(id, record)
                : await this.apiClient.create(record);
            await this.fetchList();
            runInAction(() => {
                this.isSavingInProgress = false;
            });
            return data;
        } catch (err) {
            runInAction(() => {
                this.savingError = err;
                this.isSavingInProgress = false;
            });
            throw err;
        }
    }

    async deleteRecord(id) {
        runInAction(() => {
            this.isDeletingInProgress = true;
            this.deletingError = undefined;
        });
        try {
            const data = await this.apiClient.remove(id);
            await this.fetchList();
            runInAction(() => {
                this.isDeletingInProgress = false;
            });
            return data;
        } catch (err) {
            runInAction(() => {
                this.deletingError = err;
                this.isDeletingInProgress = false;
            });
            throw err;
        }
    }

    resetSearch() {
        runInAction(() => {
            this.searchResults = [];
            this.searchTotal = 0;
            this.searchName = "";
            this.searchTypeId = undefined;
            this.searchLat = undefined;
            this.searchLng = undefined;
            this.searchPageNumber = 1;
            this.selected = undefined;
        });
    }
}

decorate(ZonesStore, {
    list: observable,
    total: observable,
    typeId: observable,
    name: observable,
    pageNumber: observable,
    pageSize: observable,
    selected: observable,
    searchResults: observable,
    searchTotal: observable,
    searchName: observable,
    searchTypeId: observable,
    searchLat: observable,
    searchLng: observable,
    searchPageNumber: observable,
    searchPageSize: observable,
    isSearchInProgress: observable,
    searchError: observable,
    regions: observable,
    isFetchingRegionsInProgress: observable,
    isImportInProgress: observable,
    importError: observable,
    isFetchingListInProgress: observable,
    fetchingListError: observable,
    isFetchingRecordInProgress: observable,
    fetchingRecordError: observable,
    isSavingInProgress: observable,
    savingError: observable,
    isDeletingInProgress: observable,
    deletingError: observable,
    fetchList: action,
    searchZones: action,
    fetchRegions: action,
    importZone: action,
    fetchRecord: action,
    saveRecord: action,
    deleteRecord: action,
    resetSearch: action,
});

export default ZonesStore;
